/**
 * Permission descriptions - explains what each permission does
 * Used in the permissions modal to help admins understand permission impact
 */

export const permissionDescriptions: Record<string, string> = {
  // Top-level permissions
  dashboard: "Access to the main dashboard overview and statistics. Used in: Dashboard page",
  notifications: "Receive and view system notifications. Used in: Notifications dropdown",
  holidays: "View the company holiday calendar. Used in: Calendar view",
  adminSettings: "Access administrative settings and configuration. Used in: Admin Settings page",
  superAdminPanel: "Access the super administrator control panel. Used in: Super Admin panel",

  // Attendance permissions
  "attendance.admin":
    "Manage and review attendance records for all employees. Used in: Attendance > Admin Attendance tab",
  "attendance.employee":
    "View and submit personal attendance records. Used in: Attendance > Personal Attendance tab",
  "attendance.viewAll":
    "View attendance records for all employees. Used in: Attendance Reports and team dashboards",
  "attendance.viewSelf": "View own attendance records only. Used in: Personal Attendance records",

  // Employees permissions
  "employees.allEmployees":
    "Show all employees list in the navigation menu. Used in: Navigation > Employees menu",
  "employees.department":
    "View department information under employees section. Used in: Employees > Department subsection",
  "employees.leaveEmployees":
    "Access the leave employees management section. Used in: Employees > Leave Employees page",
  "employees.adminLeave":
    "View and manage all employee leave requests. Used in: Employees > Admin Leave Management tab",
  "employees.leaveConfiguration":
    "Configure leave policies and settings. Used in: Employees > Leave Configuration page",
  "employees.viewProfile":
    "View employee profiles and personal details. Used in: Employee Profile page and directory",
  "employees.addleave":
    "Submit new personal leave requests. Used in: Employees > Add Leave form",
  "employees.employeeleaveEdit":
    "Edit and modify existing leave requests. Used in: Leave request edit functionality",
  "employees.adminleaveReject":
    "Reject employee leave requests as an admin. Used in: Admin Leave Management > Reject button",
  "employees.adminLeaveApproved":
    "Approve employee leave requests as an admin. Used in: Admin Leave Management > Approve button",
  "employees.userBalance":
    "View employee leave balance information. Used in: Leave Employees table and balance view",
  "employees.balanceSummaryDownload":
    "Download the leave balance summary report. Used in: Reports > Leave Balance download",

  // Reports permissions
  "reports.leaveReports":
    "View and analyze leave reports and statistics. Used in: Reports > Leave Reports section",
  "reports.attendanceReports":
    "View and analyze attendance reports and patterns. Used in: Reports > Attendance Reports section",
  "reports.leaveHistoryReport":
    "Access leave history and balance reports. Used in: Reports > Leave History & Balance tab",
  "reports.leaveReportDownload":
    "Download leave report files in various formats. Used in: Leave Reports > Download button",
  "reports.attendancereportDownload":
    "Download attendance report files in various formats. Used in: Attendance Reports > Download button",

  // Department permissions
  "department.view": "View the list of departments and their details. Used in: Department list table",
  "department.add": "Create new departments in the system. Used in: Department management > Add button",
  "department.update":
    "Edit and modify existing department details. Used in: Department edit form",
  "department.delete":
    "Remove departments from the system. Used in: Department list > Delete button",

  // Organization Management permissions
  "organization_management.memberAdd":
    "Add new members to the organization. Used in: Admin Settings > Add Members button",
  "organization_management.organizationUpdate":
    "Update organization settings, branding, and configuration. Used in: Admin Settings > Organization form",

  // Permission Management permissions
  "permission_management.view_user_permissions":
    "View permission settings assigned to users. Used in: Users page permission viewing",
  "permission_management.edit_users_permissions":
    "Modify and update permission assignments for users. Used in: Users > Edit permissions modal",

  // Role Management permissions
  "role_management.create_role":
    "Create new custom roles in the system. Used in: Roles Management > Create button",

  // Employment History permissions
  "employment_history.add":
    "Add employment history records for users. Used in: User Profile > Employment History > Add button",
  "employment_history.update":
    "Edit existing employment history entries. Used in: User Profile > Employment History edit form",
  "employment_history.delete":
    "Remove employment history records from the system. Used in: User Profile > Employment History > Delete button",

  // Configuration Management permissions
  "configuration_management.user_configrations_hours":
    "Configure working hours and schedules for individual users. Used in: User Profile > Working Hours configuration",
  "configuration_management.department_configrations_hours":
    "Configure working hours and schedules at the department level. Used in: Department management > Working Hours config",
  "configuration_management.program_configrations":
    "Configure program-level working hours and schedules. Used in: Program management > Working Hours configuration",

  // Program Management permissions
  "program_management.create":
    "Create new programs in the system. Used in: Program Management > Create button",
  "program_management.update":
    "Edit and modify existing programs. Used in: Program edit form",
  "program_management.delete":
    "Delete programs from the system. Used in: Program list > Delete button",

  // User Profile permissions
  "userProfile.update_profile":
    "Update basic profile information. Used in: User Profile > General tab",
  "userProfile.view_profile":
    "View user profile pages and information. Used in: User Profile page",
  "userProfile.update_personal_info":
    "Edit personal information (name, address, phone). Used in: User Profile > Personal Info section",
  "userProfile.update_emergency_contact":
    "Edit emergency contact details. Used in: User Profile > Emergency Contact section",
  "userProfile.update_employment_detail":
    "Edit employment details (department, type, status). Used in: User Profile > Employment Details section",

  // Holiday Management permissions
  "holiday_managment.create":
    "Add new holidays to the company calendar. Used in: Calendar > Add Holiday button",
  "holiday_managment.update":
    "Edit existing holiday entries in the calendar. Used in: Calendar > Edit Holiday form",
  "holiday_managment.delete":
    "Remove holidays from the calendar. Used in: Calendar > Delete Holiday button",

  // Ticket permissions
  "ticket.view": "View support tickets and issues. Used in: Support Tickets page",
  "ticket.create":
    "Create new support tickets and issues. Used in: Support Tickets > Create Ticket form",

  // Dashboard Access permissions
  "dashboardAccess.view_department_dashboard":
    "Access the department-level analytics and performance dashboard. Used in: Director Dashboard",
  "dashboardAccess.view_supervisor_dashboard":
    "Access the supervisor/team analytics and performance dashboard. Used in: Supervisor Dashboard",
};

/**
 * Get description for a specific permission key
 * Returns empty string if permission is not found
 */
export const getPermissionDescription = (key: string): string =>
  permissionDescriptions[key] ?? "";
